<template>
    <div :class="classList">
        <form-wrapper v-if="!ehr || (ehr && !isProcessing)">
            <primary-care-email-modal
                :isOpen="isPrimaryCareEmailModalOpen"
                @close="closePrimaryCareEmailModal"
                @request-appointment="requestAppointment($event)"
            ></primary-care-email-modal>

            <patient-agrees-modal
                v-if="isPatientAgreesModalOpen"
                @close="closePatientAgreeModal"
                @patient-agree="patientAgree"
            ></patient-agrees-modal>

            <upload-note-modal
                ref="uploadNoteModal"
                :referral="data"
                :isOpen="isUploadNoteModalOpen"
                @files-uploaded="setUploadedFiles"
                @close="closeUploadNoteModal"
            ></upload-note-modal>

            <form-row v-if="isDataChangeDisabled">
                <form-cell>
                    <span href="#" class="referral-edit-link" @click="unlockData($event)">Edit referral information</span>
                </form-cell>
            </form-row>
            <form-row>
                <form-cell width="half">
                    <boolean-field
                        :label="$t('forms.referrals.make.fields.insurance_authorization_required.label')"
                        :isRequired="true"
                        :isDisabled="isDataChangeDisabled"
                        :formData="data"
                        valuePath="isInsuranceAuthorizationRequired"
                        @change="change($event)"
                    ></boolean-field>
                </form-cell>

                <template v-if="data.isInsuranceAuthorizationRequired">
                    <form-cell width="half">
                        <radio-field
                            :label="$t('forms.referrals.make.fields.patient_primary_care_physician.label')"
                            :isRequired="true"
                            :isDisabled="isDataChangeDisabled"
                            :options="primaryCarePhysicianOptions"
                            :formData="data"
                            valuePath="isPrimaryCareOutsideMedmatch"
                            @change="change($event)"
                        ></radio-field>
                    </form-cell>
                </template>
            </form-row>

            <template v-if="data.isInsuranceAuthorizationRequired && data.isPrimaryCareOutsideMedmatch">
                <form-group :label="$t('forms.referrals.make.groups.primary_care_physician')">
                    <form-row>
                        <form-cell width="half">
                            <text-field
                                :label="$t('forms.referrals.make.fields.physician_full_name.label')"
                                :placeholder="$t('forms.referrals.make.fields.physician_full_name.placeholder')"
                                :isRequired="true"
                                :isDisabled="isDataChangeDisabled"
                                :formData="data"
                                valuePath="primaryCareName"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>

                        <form-cell width="half">
                            <text-field
                                :label="$t('forms.referrals.make.fields.physician_email_address.label')"
                                :placeholder="$t('forms.referrals.make.fields.physician_email_address.placeholder')"
                                :hint="$t('forms.referrals.make.fields.physician_email_address.hint')"
                                :formData="data"
                                :isRequired="false"
                                :isDisabled="isDataChangeDisabled"
                                valuePath="primaryCareEmail"
                                type="email"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell width="half">
                            <phone-field
                                :label="$t('forms.referrals.make.fields.physician_phone_number.label')"
                                :placeholder="$t('common.phone_number.placeholder')"
                                :isRequired="true"
                                :isDisabled="isDataChangeDisabled"
                                :formData="data"
                                valuePath="primaryCarePhone"
                                @change="change($event)"
                                @country-code-change="prefillData($event)"
                            ></phone-field>
                        </form-cell>

                        <form-cell width="half">
                            <text-field
                                :label="$t('forms.referrals.make.fields.physician_fax_number.label')"
                                :placeholder="$t('forms.referrals.make.fields.physician_fax_number.placeholder')"
                                :isRequired="false"
                                :formData="data"
                                :isDisabled="isDataChangeDisabled"
                                valuePath="primaryCareFax"
                                @change="change($event)"
                                @country-code-change="prefillData($event)"
                            ></text-field>
                        </form-cell>
                    </form-row>
                </form-group>
            </template>

            <form-row>
                <form-cell width="half">
                    <suggest-field
                        :label="$t('forms.referrals.make.fields.diagnosis.label')"
                        :placeholder="$t('forms.referrals.make.fields.diagnosis.placeholder')"
                        :hasAutofocus="true"
                        :isRequired="true"
                        :formData="data"
                        :suggestionsCall="diagnosisSuggestionsCall"
                        :isSuggested="isDiagnosisSuggested"
                        :isDisabled="isDataChangeDisabled"
                        valuePath="diagnosis"
                        @change="change($event)"
                        @select="updateDiagnosisData($event)"
                    ></suggest-field>
                </form-cell>

                <form-cell width="half">
                    <select-field
                        v-if="isSelfReferred"
                        :label="$t('forms.referrals.make.fields.patient_insurance.label')"
                        :placeholder="$t('forms.referrals.make.fields.patient_insurance.label')"
                        :options="insurancePlanOptions"
                        :isRequired="true"
                        :formData="data"
                        valuePath="insurancePlanId"
                        @change="change($event)"
                    ></select-field>
                    <form-field
                        v-else
                        :label="$t('forms.referrals.make.fields.patient_insurance.label')"
                        class="insurance-plan-name-holder"
                    >
                        <div v-if="insurancePlan">
                            <b>{{ insurancePlan.label }}</b>
                        </div>
                        <div v-else>
                            <b>{{ $t('forms.referrals.make.fields.patient_insurance.not_selected') }}</b>
                        </div>
                    </form-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.referrals.make.fields.insurance_group.label')"
                        :placeholder="$t('forms.referrals.make.fields.insurance_group.placeholder')"
                        :isRequired="false"
                        :formData="data"
                        valuePath="insuranceGroupNumber"
                        @change="change($event)"
                    ></text-field>
                </form-cell>

                <form-cell width="half">
                    <text-field
                        :label="$t('forms.referrals.make.fields.insurance_id.label')"
                        :placeholder="$t('forms.referrals.make.fields.insurance_id.placeholder')"
                        :isRequired="false"
                        :formData="data"
                        valuePath="insuranceNumber"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <div class="referral-details__notification">
                {{ $t('pages.referrals.show.notifications.patient_information_disclaimer') }}
            </div>

            <form-row>
                <form-cell class="referral-details__patient">
                    <patient-fragment
                        :formData="data"
                        :practiceId="!$auth.isUserPracticeOther() ? $auth.getPractice().id : null"
                        :isDisabled="isDataChangeDisabled"
                        @change="change($event)"
                        @country-code-change="prefillData($event)"
                    ></patient-fragment>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell class="referral-details__text-editor">
                    <text-editor-field
                        :placeholder="$t('forms.referrals.make.fields.note.placeholder')"
                        :label="$t('forms.referrals.make.fields.note.label')"
                        :formData="data"
                        :uploadedFiles="filesAlreadyUploaded"
                        :overlayText="$t('forms.referrals.make.fields.note.overlay')"
                        valuePath="note"
                        @change="change($event)"
                        @attach-file="openUploadNoteModal"
                        @remove-file="removeFile($event)"
                    ></text-editor-field>
                </form-cell>
            </form-row>

            <template #footer>
                <common-buttons>
                    <common-button
                        v-if="!isEprescribe"
                        :label="$t('forms.referrals.make.buttons.reset')"
                        :isSubmittable="false"
                        type="secondary"
                        size="large"
                        @click="emitEvent({ name: 'startOver' })"
                    ></common-button>

                    <common-button
                        :isProcessing="isSending"
                        :label="$t(submitLabel)"
                        type="primary"
                        size="large"
                        @click="submit()"
                    ></common-button>
                </common-buttons>
            </template>
        </form-wrapper>
        <div v-if="ehr && ehrFiles.length" class="ehr-files">
            <common-page-header :heading="'EMR RECORD TO ATTACH'"></common-page-header>
            <common-ehr-files ref="ehrFiles" :files="ehrFiles" @file-added="setUploadedEhrFile"></common-ehr-files>
        </div>
        <common-loader v-if="ehr" :isProcessing="isProcessing"></common-loader>
        <paywall-modal v-if="isPaywallModalOpen" @close="isPaywallModalOpen = false"></paywall-modal>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import ReferralsAPI from 'api/ReferralsAPI'
    import EhrAPI from 'api/EhrAPI'
    import { composeDisplayName } from 'components/common/CommonUserName'
    import InsurancePlansAPI from 'api/InsurancePlansAPI'
    import DiagnosisAPI from 'api/DiagnosisAPI'
    import UploadNoteModal from 'components/page-elements/referrals/UploadNoteModal'
    import PrimaryCareEmailModal from 'components/page-elements/referrals/PrimaryCareEmailModal'
    import PatientAgreesModal from 'components/page-elements/referrals/PatientAgreesModal'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import PaywallModal from 'components/page-elements/PaywallModal'
    import jsPDF from 'jspdf'

    export default {
        mixins: [FormMixin],

        components: {
            UploadNoteModal,
            PrimaryCareEmailModal,
            PatientAgreesModal,
            PaywallModal,
        },

        props: {
            selectedSlot: { type: Object, required: false, default: null },
            practice: { type: Object, required: false, default: null },
            location: { type: Object, required: false, default: null },
            ehr: { type: Boolean, default: false },
            submitLabel: { type: String, default: 'forms.referrals.make.buttons.send' },
            checkedValues: { type: Array, required: false, default: () => [] },
        },

        data() {
            return {
                data: {
                    fromUserId: parseInt(
                        this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser()?.id,
                    ),
                    toUserId: parseInt(this.$route.query.physicianId),
                    insurancePlanId: parseInt(this.$route.query.insurancePlanId),
                    subSpecialityId: parseInt(this.$route.query.subSpecialityId),
                    date: this.selectedSlot?.date,
                    startTime: this.selectedSlot?.startTime,
                    toPracticeId: this.practice?.id,
                    toPracticeLocationId: this.location?.id,
                    isInsuranceAuthorizationRequired: false,
                    isPrimaryCareOutsideMedmatch: false,
                    patient: {},
                },
                isPaywallModalOpen: false,
                allInsurancePlans: [],
                insurancePlan: null,
                isDiagnosisSuggested: false,
                isDataPrefilled: false,
                isDataLocked: true,
                isUploadNoteModalOpen: false,
                isPrimaryCareEmailModalOpen: false,
                isPatientAgreesModalOpen: false,
                uploadedFiles: [],
                isEprescribe: false,
                isProcessing: false,
                ehrFiles: [],
            }
        },

        computed: {
            existingReferral() {
                return this.$route.params.existingReferral || null
            },

            isSelfReferred() {
                return this.$route.query.selfReferred
            },

            insurancePlanOptions() {
                return this.$auth.getPractice().insurancePlans.map((item) => {
                    return {
                        label: item.name,
                        value: item.id,
                    }
                })
            },

            primaryCarePhysicianOptions() {
                return [
                    {
                        label: composeDisplayName(this.$auth.getActiveMember()),
                        value: false,
                    },
                    {
                        label: this.$t('forms.referrals.make.fields.patient_primary_care_physician.options.other'),
                        value: true,
                    },
                ]
            },

            diagnosisSuggestionsCall() {
                return (query) => DiagnosisAPI.getFromICD(query, this.ehr ? 'code' : 'name')
            },

            isDataChangeDisabled() {
                return this.isDataLocked && this.isDataPrefilled
            },

            filesAlreadyUploaded() {
                return this.uploadedFiles.filter((file) => !!file.file?.id)
            },
            classList() {
                return {
                    'pos-relative': this.ehr,
                }
            },
        },

        methods: {
            submit() {
                this.$auth.isUserPracticeDemo() ? this.openPaywallModal() : this.openPatientAgreeModal()
            },
            validateAndSubmitForm() {
                if (
                    this.data.isPrimaryCareOutsideMedmatch &&
                    (!this.data.primaryCareEmail || !this.data.primaryCareEmail.length)
                ) {
                    return this.openPrimaryCareEmailModal()
                }

                this.handleFormSubmission(this.data)
            },

            patientAgree(value) {
                this.data.isPatientAgreedToSms = value
                this.isPatientAgreesModalOpen = false
                this.validateAndSubmitForm()
            },

            requestAppointment(event) {
                this.closePrimaryCareEmailModal()

                if (event && event.email) {
                    this.data.primaryCareEmail = event.email
                }
                this.handleFormSubmission(this.data)
            },

            handleFormSubmission(data) {
                data.referralNoteIds = this.filesAlreadyUploaded.map((file) => file.file.id)
                if (this.isEprescribe || this.checkedValues?.length) {
                    this.emitSent(data)
                    return
                }
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    if (this.$route.query.referralTests.length === 1) {
                        data.referralTests = [Number(this.$route.query.referralTests)]
                    } else {
                        data.referralTests = this.$route.query.referralTests.map((item) => Number(item))
                    }
                }

                this.handleRequest(ReferralsAPI.create(data, this.$auth.isUserPracticeOther())).then(() => this.emitSent(data))
            },

            updateDiagnosisData({ data } = {}) {
                this.change({
                    path: 'diagnosis',
                    value: this.$get(data, 'name') || this.data.diagnosis,
                })
                this.change({
                    path: 'icdCodeId',
                    value: this.$get(data, 'id'),
                })

                this.isDiagnosisSuggested = Boolean(data)
            },

            unlockData(event) {
                if (event) {
                    event.preventDefault()
                }
                this.isDataLocked = false
            },

            openUploadNoteModal() {
                this.isUploadNoteModalOpen = true
            },

            closeUploadNoteModal() {
                this.isUploadNoteModalOpen = false
            },

            openPrimaryCareEmailModal() {
                this.isPrimaryCareEmailModalOpen = true
            },

            closePrimaryCareEmailModal() {
                this.isPrimaryCareEmailModalOpen = false
            },

            openPatientAgreeModal() {
                this.isPatientAgreesModalOpen = true
            },

            togglePaywallModal() {
                this.isPaywallModalOpen = !this.isPaywallModalOpen
            },

            openPaywallModal() {
                this.togglePaywallModal()
            },

            closePatientAgreeModal() {
                this.isPatientAgreesModalOpen = false
            },

            setUploadedFiles(files) {
                this.uploadedFiles = files
                this.isUploadNoteModalOpen = false
            },

            setUploadedEhrFile(file) {
                this.uploadedFiles.push(file)
            },

            getLatestConditionValue(arr) {
                if (!arr || arr.length === 0) {
                    return null
                }

                arr.sort((a, b) => new Date(b.resource.dateRecorded) - new Date(a.resource.dateRecorded))

                return arr[0].resource
            },
            getICD10Code(codingArray) {
                if (!codingArray || codingArray.length === 0) {
                    return null
                }

                const icd10Coding = codingArray.find((coding) => coding.system === 'http://hl7.org/fhir/sid/icd-10-us')

                return icd10Coding ? icd10Coding.code : null
            },

            removeFile(file) {
                this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file)
                this.$refs.ehrFiles.acceptedFilesIds = this.$refs.ehrFiles.acceptedFilesIds.filter((id) => id !== file.file.id)
                this.$refs.uploadNoteModal.removeFile(file)
            },
            getTemplate(json) {
                return `<pre
                        id="pdf"
                        style="
                            text-align: left;
                            width: fit-content;
                            white-space: pre-wrap;
                            font-size: 4px;
                            word-wrap: break-word;
                            max-width: 200px;
                        "
                    >
                        ${JSON.stringify(json, null, 1)}
                    </pre>`
            },
            generatePDF(template) {
                return new Promise((resolve) => {
                    const doc = new jsPDF()
                    doc.setFontSize(11)
                    doc.setFont('Courier', 'normal')
                    doc.html(template.template, {
                        callback: (doc) => {
                            resolve({ base64: doc.output('datauristring'), name: template.name })
                        },
                        x: 10,
                        y: 10,
                    })
                })
            },
        },

        created() {
            InsurancePlansAPI.getList()
                .then((val) => {
                    this.allInsurancePlans = val
                })
                .then(() => {
                    if (this.$route.query.insurancePlanId) {
                        const pId = Number(this.$route.query.insurancePlanId)
                        this.insurancePlanId = pId
                        this.insurancePlan = this.allInsurancePlans.find((ip) => ip.value === pId)
                    }
                })
        },

        mounted() {
            if (this.$route?.query?.key) {
                this.isProcessing = true
                EhrAPI.getPatientData(this.$route.query.key)
                    .then(async (res) => {
                        const patientInfo = res.patient
                        const patientFormData = {}
                        const conditionsInfo = res.condition
                        const conditionTemplate = this.getTemplate(res.condition)
                        const patientTemplate = this.getTemplate(res.patient)
                        const allergyIntoleranceTemplate = this.getTemplate(res.allergyIntolerance)
                        const diagnosticReportTemplate = this.getTemplate(res.diagnosticReport)
                        const carePlanTemplate = this.getTemplate(res.carePlan)
                        const goalTemplate = this.getTemplate(res.goal)
                        const medicationStatementTemplate = this.getTemplate(res.medicationStatement)
                        const procedureTemplate = this.getTemplate(res.procedure)

                        const templates = [
                            { template: conditionTemplate, name: 'condition.pdf' },
                            { template: patientTemplate, name: 'patient.pdf' },
                            { template: allergyIntoleranceTemplate, name: 'allergyIntolerance.pdf' },
                            { template: diagnosticReportTemplate, name: 'diagnosticReport.pdf' },
                            { template: carePlanTemplate, name: 'carePlan.pdf' },
                            { template: goalTemplate, name: 'goal.pdf' },
                            { template: medicationStatementTemplate, name: 'medicationStatement.pdf' },
                            { template: procedureTemplate, name: 'procedure.pdf' },
                        ]

                        const promises = templates.map((template) => this.generatePDF(template))

                        const pdfs = await Promise.all(promises)

                        this.ehrFiles = pdfs.map((pdf) => ({
                            base64File: pdf.base64,
                            file: {
                                name: pdf.name,
                            },
                        }))

                        const condition = this.getLatestConditionValue(conditionsInfo.entry)
                        const icd10Code = this.getICD10Code(condition?.code?.coding)
                        const icd10Response = await DiagnosisAPI.getFromICD10(icd10Code, 'code')

                        if (icd10Response.length) {
                            this.data.diagnosis = icd10Code
                            this.updateDiagnosisData(icd10Response[0])
                        }
                        if (patientInfo) {
                            const name = patientInfo.name ? patientInfo.name[0]?.text?.split(/(?<=^\S+)\s/) : []
                            patientFormData.firstName = name[0] || ''
                            patientFormData.lastName = name[1] || ''
                            patientFormData.dateOfBirth = patientInfo.birthDate || ''
                            patientFormData.ssn = ''

                            const phone = patientInfo.telecom
                                ? patientInfo.telecom.find((contact) => contact.system === 'phone' && contact.use === 'work')
                                : null
                            if (phone) {
                                patientFormData.mobilePhone = phone.value
                            }
                        } else {
                            patientFormData.firstName = ''
                            patientFormData.lastName = ''
                            patientFormData.dateOfBirth = ''
                            patientFormData.ssn = ''
                            patientFormData.mobilePhone = ''
                        }

                        this.data.patient = patientFormData
                    })
                    .finally(() => {
                        this.isProcessing = false
                    })
            }
            if (this.existingReferral) {
                const getProperPCValue = (field) => {
                    return this.existingReferral.isPrimaryCareOutsideMedmatch
                        ? this.existingReferral.primaryCarePhysician[field]
                        : null
                }

                const prefillData = {
                    ...this.existingReferral,
                    insurancePlanId: this.existingReferral.insurancePlan.id,
                    primaryCareName: getProperPCValue('name'),
                    primaryCareEmail: getProperPCValue('email'),
                    primaryCarePhone: getProperPCValue('phone'),
                    primaryCareFax: getProperPCValue('fax'),
                }
                this.prefillData(prefillData)
                this.isDataPrefilled = true

                //additional custom data
                this.data.patientId = this.existingReferral.patient.id
                this.data.fromReferralId = Number(this.existingReferral.id)
            }

            this.isEprescribe = this.$route.name === 'referrals.eprescribe'
        },
    }
</script>
